import {
  ApolloClient,
  defaultDataIdFromObject,
  InMemoryCache,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { ApolloLinks } from '@u-next/unextjs-oauth';
import { createForcePostRequestLink } from './createForcePostRequestLink';
import { createHttpLink } from './createHttpLink';
import {
  createPersistedQueryLink,
  type PersistedQueryManifest,
} from './createPersistedQueryLink';
import type { NormalizedCacheObject, StoreObject } from '@apollo/client';
import type { ITokenHolder } from '@u-next/unextjs-oauth';
import type { Config } from 'u-next/config';
import type { ApolloClientName } from 'u-next/custom';

class TokenHolder implements ITokenHolder {
  private tokenValue: string | undefined;
  set(newToken: string): void {
    this.tokenValue = newToken;
  }
  get(): string | undefined {
    return this.tokenValue;
  }
}

export function initApollo(
  config: Config,
  name: ApolloClientName,
  persistedQueryManifest?: PersistedQueryManifest
): ApolloClient<NormalizedCacheObject> {
  const isBrowser = typeof window !== 'undefined';
  const apiEndpoint = config.COMMAND_CENTER;
  const accessTokenHolder = new TokenHolder();

  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser,
    name,
    version: process.env.COMMIT_TAG,
    link: from([
      ApolloLinks.createRetryLink({
        delay: {
          initial: 300,
          max: Infinity,
          jitter: true,
        },
        attempts: {
          max: 5,
          retryIf: (error, _operation) => {
            if (
              !!error &&
              !_operation.query.definitions.some(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (node: any) =>
                  node.kind === 'OperationDefinition' &&
                  node.operation === 'mutation'
              )
            ) {
              return true;
            }
            return false;
          },
        },
      }),
      onError(
        ApolloLinks.createErrorLinkWithOAuthRefresh(
          isBrowser,
          accessTokenHolder
        )
      ),
      ApolloLinks.createAuthLink(isBrowser, accessTokenHolder),
      ...(persistedQueryManifest
        ? [
            createPersistedQueryLink({
              isLocalDev: process.env.NODE_ENV === 'development',
              persistedQueryManifest,
            }),
          ]
        : []),
      createForcePostRequestLink(),
      createHttpLink({
        uri: apiEndpoint,
        headers: {},
      }),
    ]),
    cache: new InMemoryCache({
      dataIdFromObject: (o: Readonly<StoreObject>) =>
        defaultDataIdFromObject(o),
    }),
  });
}
